import React, { useCallback, useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import Input from "../../ui/common/Input";
import Button from "../../ui/common/Button";
import TextArea from "../../ui/common/TextArea";
import ToggleSwitch from "../../ui/toggleSwitch/ToggleSwitch";
import Select from "../../ui/select/Select";
import { useAppDispatch } from "../../../store";
import { updateImport, createImport, resetImportStates } from "../../../store/slices/imports";
import EncryptedAuthField from "./EncryptedAuthField";
import Api from "../../../store/api/Api";
import "./EditImportModal.scss";
import cronstrue from "cronstrue";
import cronParser from "cron-parser";

const formatDefaultValue = (value: any, type: string) => {
  if (type === "array" || Array.isArray(value)) {
    if (!Array.isArray(value)) return value;
    if (value.length === 0) return "";
    if (typeof value[0] === "object") {
      return JSON.stringify(value, null, 2);
    }
    if (
      value.length === 1 &&
      typeof value[0] === "string" &&
      value[0].substring(0, value[0].length - 1) !== ","
    ) {
      return value[0] + ",";
    }
    return value.join(",\n");
  }
  if (type === "object" || (value !== null && typeof value === "object")) {
    return JSON.stringify(value, null, 2);
  }
  return value;
};

const EditImportModal = ({ data, onClose, newImport }) => {
  const dispatch = useAppDispatch();
  const api = useMemo(() => new Api(), []);
  const importTypes = useSelector((state: any) => state.imports.importTypes);
  const importId = data.id || "";
  const [brandId, setBrandId] = useState(data.brandId || "");
  const [type, setType] = useState(data.type || "");
  const [cronTime, setCronTime] = useState(data.cronTime || "* * * * *");
  const [importActive, setImportActive] = useState(data.active || false);
  const [insertMethod, setInsertMethod] = useState(data.insertMethod || "");
  const [mapping, setMapping] = useState(JSON.stringify(data.mapping || {}, null, 2));
  const [params, setParams] = useState<any>({});
  const [decryptedAuthData, setDecryptedAuthData] = useState<any>(null);
  const [currentAuthEncrypted, setCurrentAuthEncrypted] = useState(data.authEncrypted || "");
  const [currentAuthIv, setCurrentAuthIv] = useState(data.authIv || "");
  const [currentAuthTag, setCurrentAuthTag] = useState(data.authTag || "");
  const [hasUnencryptedChanges, setHasUnencryptedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [availableNewsletterConfigs, setAvailableNewsletterConfigs] = useState<any[]>([]);
  const InsertMethods = ["insert", "update"];

  // Check if this import has encrypted auth
  const hasEncryptedAuth = useMemo(() => {
    return currentAuthEncrypted && currentAuthIv && currentAuthTag;
  }, [currentAuthEncrypted, currentAuthIv, currentAuthTag]);

  const updatingImport = useSelector((state: any) => state.imports.updatingImport);
  const updatingImportError = useSelector((state: any) => state.imports.updatingImportError);
  const creatingImport = useSelector((state: any) => state.imports.creatingImport);
  const creatingImportError = useSelector((state: any) => state.imports.creatingImportError);
  const [hasAttemptedUpdate, setHasAttemptedUpdate] = useState(false);
  const currentBrand = useSelector((state: any) => state.currentBrand.data);


  const humanReadableCron = useMemo(() => {
    try {
      return cronstrue.toString(cronTime);
    } catch {
      return "";
    }
  }, [cronTime]);

  const cronNextIteration = useMemo(() => {
    try {
      return `Next iteration: ${cronParser.parseExpression(cronTime).next().toString()}`;
    } catch {
      return "";
    }
  }, [cronTime]);

  const timezone = useMemo(() => {
    return currentBrand.domainSettings.timezone;
  }, [currentBrand]);

  useEffect(() => {
    // set initial values of param fields based on type and initial data
    if (!type) return;
    const availableParams = importTypes[type];

    if (!availableParams) return;

    let preparedValuesForParams = {};
    availableParams.forEach((element) => {
      if (data[element.field] !== undefined) {
        if (element.field === "wildcardFilters" || element.field === "filters") {
          preparedValuesForParams = {
            ...preparedValuesForParams,
            [element.field]: JSON.stringify(data[element.field], null, 2),
          };
        } else if (data[element.field] && element.type === "array") {
          // Check if array contains objects or strings
          const isArrayOfObjects = Array.isArray(data[element.field]) &&
            data[element.field].length > 0 &&
            typeof data[element.field][0] === 'object';

          if (isArrayOfObjects) {
            // Handle array of objects (like mapping array)
            preparedValuesForParams = {
              ...preparedValuesForParams,
              [element.field]: JSON.stringify(data[element.field], null, 2)
            };
          } else if (data[element.field].length === 1 &&
            typeof data[element.field][0] === 'string' &&
            data[element.field][0].substring(0, data[element.field][0].length - 1) !== ",") {
            preparedValuesForParams = {
              ...preparedValuesForParams,
              [element.field]: data[element.field][0] + ","
            };
          } else {
            preparedValuesForParams = {
              ...preparedValuesForParams,
              [element.field]: (!Array.isArray(data[element.field])) ? data[element.field] + ',' : data[element.field].join(",\n")
            };
          }
        } else if (data[element.field] !== undefined) {
          preparedValuesForParams = {
            ...preparedValuesForParams,
            [element.field]: data[element.field],
          };
        }
      } else if (newImport && element.default !== undefined) {
        preparedValuesForParams = {
          ...preparedValuesForParams,
          [element.field]: formatDefaultValue(element.default, element.type),
        };
      }
    });

    if (newImport) {
      setParams(preparedValuesForParams);
      const insertMethodDefault = availableParams.find((param) => param.field === "insertMethod")?.default;
      if (insertMethodDefault !== undefined) {
        setInsertMethod(insertMethodDefault);
      }
    } else {
      setParams(prevParams => ({
        ...prevParams,
        ...preparedValuesForParams,
      }));
    }
  }, [importTypes, type, data, newImport]);

  useEffect(() => {
    if (Object.keys(importTypes).length > 0 && importTypes[type] === undefined && !newImport) {
      alert("Unfortunately, we don't have good param information about this import type. Add the import type to the endpoint 'api/admin/importTypes'.");
    }
  }, [importTypes, type, newImport]);

  const handleParamValueChange = useCallback((value: any, field: string) => {
    setParams(prevParams => ({
      ...prevParams,
      [field]: value,
    }));
  }, []);

  const closeModal = useCallback(() => {
    onClose();
  }, [onClose]);

  const prepareParamsForSave = useCallback((paramsData) => {
    let params = { ...paramsData };
    if (!importTypes[type]) return params;
    for (let key in params) {
      if (params.hasOwnProperty(key)) {
        const paramDefinition = importTypes[type].find((param) => param.field === key);
        if (!paramDefinition) continue;
        const paramType = paramDefinition.type;
        if (params[key] && paramType === "array" && typeof params[key] === "string" && params[key].includes(",")) {
          if (key === "wildcardFilters" || key === "filters") {
            try {
              params[key] = JSON.parse(params[key]);
            } catch (e) {
              console.error("Error parsing wildcardFilters", e);
            }
          } else {
            params[key] = params[key].split(",").map((item) => item.trim()).filter(item => item);
          }
        }
        if (params[key] && (paramType === "number" || paramType === "integer")) {
          const numericValue = Number(params[key]);
          if (Number.isFinite(numericValue)) params[key] = numericValue;
        }
        if (params[key] && paramType === "boolean") {
          params[key] = (params[key] === "true" || params[key] === true);
        }
      }
    }
    return params;
  }, [importTypes, type]);

  const handleDecryptedAuthChange = useCallback((authData: any) => {
    setDecryptedAuthData(authData);

    // If authData contains encrypted fields, update the current encrypted state
    if (authData && authData.authEncrypted) {
      setCurrentAuthEncrypted(authData.authEncrypted);
      setCurrentAuthIv(authData.authIv);
      setCurrentAuthTag(authData.authTag);
    }
  }, []);

  const handleSaveImport = useCallback(async () => {
    try {
      let preparedParams = {} as any;
      preparedParams = prepareParamsForSave(params);

      // If we have decrypted auth data, include it in the payload
      if (decryptedAuthData) {
        preparedParams = {
          ...preparedParams,
          ...decryptedAuthData,
        };
      }

      // If we have encrypted auth, always include the current encrypted fields
      if (hasEncryptedAuth && currentAuthEncrypted) {
        preparedParams.authEncrypted = currentAuthEncrypted;
        preparedParams.authIv = currentAuthIv;
        preparedParams.authTag = currentAuthTag;
      }

      const payload = {
        ...data,
        active: importActive,
        brandId,
        cronTime,
        type,
        mapping: JSON.parse(mapping),
        ...preparedParams,
      };

      if (newImport) {
        await dispatch(createImport(payload));
      } else {
        await dispatch(updateImport(importId, payload));
      }
      setHasAttemptedUpdate(true);
    } catch (err) {
      console.error(err);
    }
  }, [dispatch, importActive, brandId, cronTime, type, params, mapping, importId, data, prepareParamsForSave, decryptedAuthData, hasEncryptedAuth, currentAuthEncrypted, currentAuthIv, currentAuthTag, newImport]);

  const saveDisabledReason = useMemo(() => {
    const reasons = [
      isSaving && "Import is being saved",
      !type && "Type is required",
      !brandId && "BrandId is required",
      !cronTime && "CronTime is required",
    ].filter(Boolean);

    return reasons.length > 0 ? `Cannot save: ${reasons.join(", ")}` : "";
  }, [isSaving, type, brandId, cronTime]);

  const validateAndSave = useCallback(async () => {
    // Check if there are unencrypted changes BEFORE starting the async save
    if (hasUnencryptedChanges) {
      const confirmed = window.confirm(
        "You have unencrypted changes in the authentication fields.\n\n" +
        "Please choose an option:\n" +
        "- Click 'OK' to discard changes and save the form\n" +
        "- Click 'Cancel' to go back and encrypt the changes first"
      );

      if (!confirmed) {
        // User wants to go back and encrypt - don't save
        return;
      }
      // User chose to discard changes, continue with save
    }

    // No unencrypted changes or user confirmed, proceed with save
    setIsSaving(true);
    try {
      await handleSaveImport();
    } finally {
      setIsSaving(false);
    }
  }, [hasUnencryptedChanges, handleSaveImport]);

  useEffect(() => {
    if (hasAttemptedUpdate && !updatingImport && !creatingImport) {
      if (updatingImportError === null && creatingImportError === null) {
        closeModal();
      }
    }
  }, [hasAttemptedUpdate, updatingImport, updatingImportError, creatingImport, creatingImportError, closeModal]);

  // Load newsletter credentials from API endpoint for credential copying
  useEffect(() => {
    if (type === 'newsletter') {
      api.getNewsletterCredentials()
        .then((response: any) => {
          // Filter out current config's label if it exists
          const available = response.filter((c: any) => c.imapLabel !== params.imapLabel);
          setAvailableNewsletterConfigs(available);
        })
        .catch((err: any) => {
          console.error('Failed to load newsletter credentials:', err);
          setAvailableNewsletterConfigs([]);
        });
    }
  }, [type, params.imapLabel, api]);

  useEffect(() => {
    // Reset the imports update error state when the modal is opened
    dispatch(resetImportStates());
  }, [dispatch]);



  const paramsForm = useMemo(() => {
    if (importTypes[type] === undefined || Object.keys(params).length === 0) return "";

    // Filter out encrypted auth fields - these should NEVER be shown in the params form
    // They are handled by the EncryptedAuthField component instead
    // Also filter out imapLabel for newsletter type - it has its own dedicated input field
    const encryptedAuthFields = ['authEncrypted', 'authIv', 'authTag'];
    const filteredParams = importTypes[type].filter(param => {
      // Always hide encrypted auth fields
      if (encryptedAuthFields.includes(param.field)) {
        return false;
      }
      // Hide imapLabel for newsletter - it has a dedicated input field above
      if (type === 'newsletter' && param.field === 'imapLabel') {
        return false;
      }
      return true;
    });

    return (
      <div className="params-container">
        {filteredParams.map((param, i) => {
          return (
            <div className="param-container" key={param.field || i}>
              <label className="param-label">{param.field}</label>
              {param.type === "boolean" ? (
                <ToggleSwitch
                  checked={params[param.field] === true}
                  onChange={(e) => {
                    handleParamValueChange(e, param.field);
                  }}
                />
              ) : param.type === "array" || param.type === "object" ? (
                <TextArea
                  value={params[param.field] ?? ""}
                  onChange={(e) => {
                    handleParamValueChange(e.target.value, param.field);
                  }}
                />
              ) : (
                <Input
                  value={params[param.field] ?? ""}
                  onChange={(e) => {
                    handleParamValueChange(e.target.value, param.field);
                  }}
                  fullWidth
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }, [params, handleParamValueChange, importTypes, type]);

  return (
    <div className="imports-modal-wrapper">
      <h1 className="imports-modal-title">{`${newImport ? 'Add new' : 'Edit'} import type: ${type || ''}`}</h1>
      <div className="imports-modal-container">
        <div className="column narrow">
          <Input label="Id" value={importId} disabled separateLines fullWidth />

          <div className="drop-select">
            <label htmlFor="importType">Type</label>

            {newImport && !type ? (
              <Select
                options={Object.keys(importTypes).sort()}
                selectedOption={type || ""}
                setSelectedOption={(e) => {
                  setType(e);
                }}
                styling={{
                  selectOptionsContainerClassName: "types-options-dropdown",
                }}
                fullWidth
                search
              />
            ) : (
              <Input
                value={type}
                disabled={true}
                onChange={(e) => {
                  setType(e.target.value);
                }}
                fullWidth
              />
            )}

          </div>
          <div className="drop-select">
            <label htmlFor="type">BrandId</label>
            <Input
              value={brandId}
              disabled={true}
              onChange={(e) => {
                setBrandId(e.target.value);
              }}
              fullWidth
            />
          </div>
          <div className="switch-container">
            <ToggleSwitch
              checked={importActive}
              onChange={(e) => {
                setImportActive(e === true);
              }}
            />{" "}
            Active
          </div>
          {type && (
            <div className="drop-select">
              <label htmlFor="insertMethod">Insert Method</label>
              <Select
                options={InsertMethods}
                selectedOption={insertMethod}
                setSelectedOption={(e) => {
                  setInsertMethod(e);
                  handleParamValueChange(e, "insertMethod");
                }}
                styling={{
                  selectOptionsContainerClassName: "options-dropdown",
                }}
                fullWidth
              />
            </div>
          )}
        </div>
        <div className="column narrow">
          <p>Mapping</p>
          <TextArea
            value={mapping}
            className="textarea-code-editor"
            onChange={(e) => {
              setMapping(e.target.value);
            }}
          />

          <p>CronTime (based on {timezone} timezone)</p>
          <p>{humanReadableCron}</p>
          <p>{cronNextIteration}</p>


          <Input
            label=""
            value={cronTime ? cronTime : "* * * * *"}
            onChange={(e) => {
              setCronTime(e.target.value ? e.target.value : "* * * * *");
            }}
            fullWidth
          /></div>
        <div className="column narrow">
          {hasEncryptedAuth && (
            <EncryptedAuthField
              authEncrypted={currentAuthEncrypted}
              authIv={currentAuthIv}
              authTag={currentAuthTag}
              onDecryptedChange={handleDecryptedAuthChange}
              onHasUnencryptedChanges={setHasUnencryptedChanges}
            />
          )}

          {type === 'newsletter' && availableNewsletterConfigs.length > 0 && (
            <div className="copy-credentials-section">
              <label>Copy credentials from existing IMAP account</label>
              <Select
                options={[
                  { value: '', label: 'Select an IMAP account to copy credentials from...' },
                  ...availableNewsletterConfigs.map((cred: any) => ({
                    value: cred.imapLabel,
                    label: cred.imapLabel
                  }))
                ]}
                selectedOption=""
                setSelectedOption={(selectedLabel: string) => {
                  if (!selectedLabel) return;
                  const sourceCred = availableNewsletterConfigs.find((c: any) => c.imapLabel === selectedLabel);
                  if (sourceCred) {
                    setCurrentAuthEncrypted(sourceCred.authEncrypted);
                    setCurrentAuthIv(sourceCred.authIv);
                    setCurrentAuthTag(sourceCred.authTag);
                    handleParamValueChange(sourceCred.imapLabel, 'imapLabel');
                    alert(`Credentials copied from: ${sourceCred.imapLabel}`);
                  }
                }}
                styling={{
                  selectOptionsContainerClassName: "options-dropdown",
                }}
                fullWidth
              />
            </div>
          )}

          {paramsForm}
        </div>
      </div>
      <div className="sub-modal-error">
        {updatingImportError && (
          <div className="error-message">
            <strong>Failed updating import:</strong> <code>{updatingImportError.message}</code>
          </div>
        )}
        {creatingImportError && (
          <div className="error-message">
            <strong>Failed creating import:</strong> <code>{creatingImportError.message}</code>
          </div>
        )}
      </div>
      <div className="sub-modal-actions">
        <Button
          variant="danger"
          onClick={() => {
            closeModal();
          }}
        >
          Cancel
        </Button>
        <span title={saveDisabledReason}>
          <Button
            variant="success"
            onClick={validateAndSave}
            disabled={isSaving || !type || !brandId || !cronTime}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </span>
      </div>
    </div>
  );
};

export default EditImportModal;
